import { useContext, useEffect, useState } from 'react';

import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Typography,
  Box,
} from '@mui/material';
import { formatCurrency, formatDateTime } from '../../../../utility/utils';
import { api, Booking, BookingErrorStatus, BookingRemark, BookingStatusChangeError, IBookingCheckOut, IBookingSalesAndPaymentsResult, RemarkType, RequestStatus, SourceApp, VehicleStatus } from '../../../../logic/api';

import ErrorDialog from "../../../../components/notifications/errorDialog/ErrorDialog";
import { ProgressDialog } from "../../../../components/notifications/progressDialog/progressDialog";
import { SessionContext } from '../../../../utility/useSession';
import { ReleaseFlag, useReleaseManagement } from '../../../../utility/feature-management';
import { useTranslation } from 'react-i18next';
import { FaSpinner } from 'react-icons/fa';
import { useDate } from '../../../../utility/useDate';

interface ConfirmCheckOutDialog2Props {
  booking: Booking | null;
  salesAndPayments: IBookingSalesAndPaymentsResult | null;
  display: boolean;
  showPaymentDialog: () => void;
  refresh: () => void;
  cancel: () => void;
  onCheckedOut: (booking: Booking | null) => void;
}

const CheckoutConfirmDialogV2 = ({
  booking,
  salesAndPayments,
  display,
  showPaymentDialog,
  refresh,
  cancel,
  onCheckedOut,
}: ConfirmCheckOutDialog2Props) => {
  const session = useContext(SessionContext);
  const showCustomerRemarksRelease = useReleaseManagement(ReleaseFlag.releaseCustomerRemarksAtCheckout)
  const [isVehicleReady, setIsVehicleReady] = useState<boolean>(false);
  const [customerRemarks, setCustomerRemarks] = useState<BookingRemark[]>([]);
  const preValidateCheckoutHook = api.hooks.booking.preValidateStatusChange();
  const listRemarks = api.hooks.booking.listRemarks();

  // Make call to pre-validate the status change on display first time
  const preValidateCheckout = () => booking && preValidateCheckoutHook.send(session.current.context, { id: booking.id, vehicleStatus: VehicleStatus.CheckedOut });
  useEffect(() => {
    if (!display || !booking) {
      return;
    }

    preValidateCheckout();
  }, [display, booking])

  useEffect(() => {
    if (booking?.id) {
      listRemarks.send(session.current.context, {
        bookingId: booking.id
      }, (result) => {
        if (result) {
          const filteredCustomerRemarks = result.filter(remark =>
            remark.remarkType !== RemarkType.Internal &&
            remark.remarkType !== null &&
            !remark.deletedAt
          );
          setCustomerRemarks(filteredCustomerRemarks);
        }
      });
    }
  }, [booking?.id]);

  // Do nothing if not visible
  if (!display) {
    return <></>
  }

  // Show loading dialog of still loading
  const loading = !booking || preValidateCheckoutHook.status === RequestStatus.loading;
  if (loading) {
    return <ProgressDialog status={RequestStatus.loading} />
  }

  // Calculate current state
  const earlyReturnEnabled = session.current.site?.preferences.early_return_enabled || false;
  const earlyReturnChangeError = preValidateCheckoutHook.data?.errors.find((e) => e.status === BookingErrorStatus.EarlyReturn);
  const earlyReturnPending = earlyReturnEnabled && !(booking.labels || []).includes("early-return") && earlyReturnChangeError
  const paymentRequired = !session.current.site?.preferences.booking_checkout_with_balance_enabled || false;
  const paymentPending = salesAndPayments?.outstanding || 0;
  const vehicleInReturns = booking.vehicle?.location?.returns ?? true;
  const vehicleKeyInReturns = booking.vehicleKey?.location?.returns ?? true;
  const allowCheckoutFromBlockPark = session.current.site?.preferences.workflowRules?.bookingCheckoutAllowFromBlockPark || false;
  const movementPending = !allowCheckoutFromBlockPark && (!vehicleInReturns || !vehicleKeyInReturns);
  const showRemarks = showCustomerRemarksRelease && customerRemarks.length > 0;

  let dialogState: string;
  let dialogStateComponent: JSX.Element;
  if (earlyReturnPending && !isVehicleReady) {
    dialogState = "early-return";
    dialogStateComponent = <EarlyReturnConfirmState booking={booking} earlyReturnChangeError={earlyReturnChangeError} cancel={cancel} onVehicleReady={() => setIsVehicleReady(true)} onApplied={refresh} />;
  } else if (showRemarks) {
    dialogState = "customer-remarks";
    dialogStateComponent = <ShowCustomerRemarks remarks={customerRemarks} error={listRemarks.err} status={listRemarks.status} onCancel={cancel} onContinue={() => {
      // Move to next state by setting showRemarks to empty
      // This is a bit of a hack to get the dialog to go to the next possible state
      setCustomerRemarks([]);
    }} />
  } else if (paymentRequired && paymentPending) {
    dialogState = "payment-pending";
    dialogStateComponent = <PaymentRequiredState cancel={cancel} showPaymentDialog={showPaymentDialog} amountDue={salesAndPayments?.outstanding || 0} />;
  } else if (movementPending) {
    dialogState = "movement-pending";
    dialogStateComponent = <InBlockParkState cancel={cancel} vehicleInReturns={vehicleInReturns} vehicleKeyInReturns={vehicleKeyInReturns} />;
  } else {
    dialogState = "checkout-prompt";
    dialogStateComponent = <CheckOutPromptState booking={booking} amountDue={salesAndPayments?.outstanding || 0} cancel={cancel} onCheckedOut={onCheckedOut} />;
  }

  return <Dialog fullWidth={true} maxWidth="sm" open={display} data-testid="checkout-dialog" data-checkout-state={dialogState}>
    {dialogStateComponent}
  </Dialog>;
}

interface EarlyReturnConfirmStateProps {
  booking: Booking;
  earlyReturnChangeError: BookingStatusChangeError;
  onApplied: () => void;
  onVehicleReady: () => void;
  cancel: () => void;
}

const EarlyReturnConfirmState = ({
  booking,
  earlyReturnChangeError,
  onApplied,
  onVehicleReady,
  cancel,
}: EarlyReturnConfirmStateProps) => {
  const { t } = useTranslation();
  const session = useContext(SessionContext);
  const [isApplying, setIsApplying] = useState(false);
  const applyStatusChangeErrorActions = api.hooks.booking.applyStatusChangeErrorActions();

  const requestEarlyReturn = () => {
    setIsApplying(true);
    applyStatusChangeErrorActions.send(
      session.current.context,
      {
        errors: [earlyReturnChangeError],
        id: booking.id,
      },
      () => {
        setTimeout(() => {
          setIsApplying(false);
          onApplied()
        }, 500);
      }
    );

    return true;
  };

  return (<>
    <DialogTitle data-testid="checkoutDialogTitle">{t("booking.checkoutDialog.earlyReturn.title")}</DialogTitle>

    <DialogContent>
      <DialogContentText sx={{
        color: "red",
        fontWeight: "bold",
        marginBottom: "20px",
        marginTop: "0px",
      }}>
        {t(
          "booking.checkoutDialog.earlyReturn.content.expectedReturn",
          {
            expectedReturn: formatDateTime(booking.expectedReturnDate, session)
          }
        )}
      </DialogContentText>
      <DialogContentText>
        {t("booking.checkoutDialog.earlyReturn.content.earlyReturnInfo")}
      </DialogContentText>
      <DialogContentText>
        {t("booking.checkoutDialog.earlyReturn.content.continueCheckoutInfo")}
      </DialogContentText>
    </DialogContent>

    <DialogActions>
      <Button data-testid="btnCancel" onClick={() => cancel()} disabled={isApplying}>{t("general.cancel")}</Button>

      <Button data-testid="btn-continue-checkout" onClick={onVehicleReady} disabled={isApplying}>{t("booking.checkoutDialog.earlyReturn.buttons.continue")}</Button>

      <Button data-testid="btn-apply-early-return" onClick={requestEarlyReturn} disabled={isApplying}>
        <b>{t("booking.checkoutDialog.earlyReturn.buttons.earlyReturn")}</b>&nbsp;
        {isApplying && (<FaSpinner className="spin" />)}
      </Button>
    </DialogActions>
  </>);
}

interface PaymentRequiredStateProps {
  showPaymentDialog: () => void;
  amountDue: number;
  cancel: () => void;
}

const PaymentRequiredState = ({
  showPaymentDialog,
  amountDue,
  cancel,
}: PaymentRequiredStateProps) => {
  const { t } = useTranslation();
  const session = useContext(SessionContext);
  const isCheckedOutCopyReleased = useReleaseManagement(ReleaseFlag.releaseCheckoutDialogCheckedOutCopy);

  return (<>
    <DialogTitle data-testid="checkoutDialogTitle">{t("booking.checkoutDialog.paymentPending.title")}</DialogTitle>

    <DialogContent data-testid="checkoutDialogContent">
      <DialogContentText sx={{
        color: "red",
        fontWeight: "bold",
        marginBottom: "20px",
        marginTop: "0px",
      }}>
        {t("booking.checkoutDialog.paymentPending.content.amount", {
          amount: formatCurrency(amountDue, session),
        })}
      </DialogContentText>

      <DialogContentText>{t(isCheckedOutCopyReleased ? "booking.checkoutDialog.paymentPending.content.promptV2" : "booking.checkoutDialog.paymentPending.content.prompt")}</DialogContentText>
    </DialogContent>

    <DialogActions>
      <Button data-testid="btnCancel" onClick={() => cancel()}>{t("general.cancel")}</Button>

      <Button data-testid="btnRecordPayment" onClick={showPaymentDialog}>
        <b>{t("booking.checkoutDialog.paymentPending.buttons.recordPayment")}</b>
      </Button>
    </DialogActions>
  </>);
}

interface InBlockParkStateProps {
  cancel: () => void,
  vehicleInReturns: boolean,
  vehicleKeyInReturns: boolean,
}

const InBlockParkState = ({
  cancel,
  vehicleInReturns,
  vehicleKeyInReturns,
}: InBlockParkStateProps) => {
  const { t } = useTranslation();

  let contentText = "";
  if (!vehicleInReturns && !vehicleKeyInReturns) {
    contentText = t("booking.checkoutDialog.movementPending.content.neitherInReturns");
  } else if (!vehicleInReturns && vehicleKeyInReturns) {
    contentText = t("booking.checkoutDialog.movementPending.content.vehicleNotInReturns");
  } else {
    contentText = t("booking.checkoutDialog.movementPending.content.keyNotInReturns");
  }

  return (<>
    <DialogTitle data-testid="checkoutDialogTitle">{t("booking.checkoutDialog.movementPending.title")}</DialogTitle>

    <DialogContent data-testid="checkoutDialogContent">
      <DialogContentText>
        {contentText}
      </DialogContentText>
    </DialogContent>

    <DialogActions>
      <Button data-testid="btnCancel" onClick={() => cancel()}>{t("general.cancel")}</Button>
    </DialogActions>
  </>);
}

interface CheckOutPromptStateProps {
  amountDue: number;
  booking: Booking,
  cancel: () => void,
  onCheckedOut: (booking: Booking | null) => void,
}

const CheckOutPromptState = ({
  amountDue,
  booking,
  cancel,
  onCheckedOut,
}: CheckOutPromptStateProps) => {
  const session = useContext(SessionContext);
  const { t } = useTranslation();

  const requestCheckOutBooking = api.hooks.booking.checkOut();

  const checkOutBooking = () => {
    const parameters: IBookingCheckOut = {
      checkOutDate: new Date().toISOString(),
      id: booking.id,
      checkOutSource: SourceApp.frontEnd,
    };

    requestCheckOutBooking.send(
      session.current.context,
      parameters,
      onCheckedOut,
    );
  };

  const showAmountDue = Boolean(amountDue);

  return (<>
    <ErrorDialog err={requestCheckOutBooking.err}></ErrorDialog>
    <ProgressDialog status={requestCheckOutBooking.status}></ProgressDialog>

    <DialogTitle data-testid="checkoutDialogTitle">{t("booking.checkoutDialog.checkoutPrompt.title")}</DialogTitle>

    <DialogContent data-testid="checkoutDialogContent">
      {(showAmountDue) && (
        <DialogContentText sx={{
          color: "red",
          fontWeight: "bold",
          marginBottom: "20px",
          marginTop: "0px",
        }}>
          {t("booking.checkoutDialog.paymentPending.content.amount", {
            amount: formatCurrency(amountDue, session),
          })}
        </DialogContentText>
      )}
      <DialogContentText>
        {t("booking.checkoutDialog.checkoutPrompt.content")}
      </DialogContentText>
    </DialogContent>

    <DialogActions>
      <Button data-testid="btnCancel" onClick={() => cancel()}>{t("general.cancel")}</Button>

      <Button data-testid="btnConfirm" onClick={checkOutBooking}>
        <b>{t("general.confirm")}</b>
      </Button>
    </DialogActions>
  </>);
}

interface CustomerRemarksDialogProps {
  remarks: BookingRemark[];
  error: unknown;
  status: RequestStatus;
  onCancel: () => void;
  onContinue: () => void;
}

const ShowCustomerRemarks = ({
  remarks,
  error,
  status,
  onCancel,
  onContinue,
}: CustomerRemarksDialogProps) => {
  const { t } = useTranslation();
  const { toFormattedDateTimeString } = useDate();
  const [messageRead, setMessageRead] = useState(false);

  return (
    <>
      <ErrorDialog err={error} />
      <ProgressDialog status={status} />
      <DialogTitle>
        {t("booking.checkoutDialog.customerRemarks.title")}
      </DialogTitle>

      <DialogContent>
        {remarks.map((remark, index) => {
          const commentDate = toFormattedDateTimeString(new Date(remark.createDate));
          const authorName = `${remark.createdBy.firstName} ${remark.createdBy.lastName}`;

          return (
            <Box key={index} sx={{
              padding: "12px",
              marginBottom: "8px",
              backgroundColor: "#f5f5f5",
              borderRadius: "4px",
              border: "1px solid #e0e0e0"
            }}>
              <Typography sx={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}>
                {commentDate} - {authorName}
              </Typography>
              <Typography sx={{ fontSize: "14px" }}>
                {remark.remark}
              </Typography>
            </Box>
          )
        })}

        <FormControlLabel
          control={
            <Checkbox
              checked={messageRead}
              onChange={(e) => setMessageRead(e.target.checked)}
              color="primary"
            />
          }
          label={t("booking.checkoutDialog.customerRemarks.checkbox")}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel}>
          {t("general.cancel")}
        </Button>
        <Button
          onClick={onContinue}
          disabled={!messageRead}
        >
          <b>{t("general.continue")}</b>
        </Button>
      </DialogActions>
    </>
  )
}

export default CheckoutConfirmDialogV2;
